import { Inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2, DOCUMENT } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataServiceService } from './services/data-service.service';
import { HttpHelperService } from './services/http-helper.service';
import { UrlService } from './services/url.service';
import { UtilsService } from './services/utils.service';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { DomSanitizer, Meta, Title } from '@angular/platform-browser';
import { catchError, Observable, Subject, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProjectDetailsServiceService {
  public selectedProject: any;
  public projectId: any;
  public isBrowser: boolean;
  private renderer: Renderer2 | null = null;
  public lister:Subject<any>=new Subject();

  constructor(
    private titleService: Title,
    private metaService: Meta,
    public activeRouter: ActivatedRoute,
    @Inject(PLATFORM_ID) platformId: Object,
    private rendererFactory: RendererFactory2, // Inject RendererFactory2 for lazy loading of Renderer2
    public httpHelper: HttpHelperService,
    public dataService: DataServiceService,
    public utilsService: UtilsService,
    public UrlService: UrlService,
    public router:Router,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      // Initialize Renderer2 only in the browser
      this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    if(isPlatformServer(platformId)){
      this.activeRouter.params.subscribe((res: any) => {
        this.projectId = res['id'];
        this.getSelectedProject();
      });
    }
  }

  getSelectedProject(id?: any): Observable<any> {
    // this.utilsService.spinner.show();
    return this.dataService.getProjectByIdInDetails({ projectId: id }).pipe(
      tap((result: any) => {
        this.selectedProject = result || [];
        this.utilsService.spinner.hide();
        this.lister.next('data');

        if (
          !this.router.url.includes('/locations-details') &&
          !this.router.url.includes('/amenities-details') &&
          !this.router.url.includes('/paymentplan-details') &&
          !this.router.url.includes('/floorplan-details') &&
          !this.router.url.includes('/masterplan-details') &&
          !this.router.url.includes('/gallery-details')
        ) {
          this.setMetaTags();
        }


      }),
      catchError((error: any) => {
        this.utilsService.spinner.hide();
        if(error.error.message=='Project not found.'){
          this.router.navigate(['/notfound']);
        }else{
          this.utilsService.toaster.error(error.error.message || error.message);
        }

        return throwError(() => error);
      })
    );
  }

  setMetaTags() {
    this.utilsService. removeTag();
    const project = this.selectedProject?.project || {};
    const pageTitle = this.selectedProject?.projectMeta?.title || `${project.projectName} | Eplog Properties`;
    const pageDescription: any = this.selectedProject?.projectMeta?.description ||
      this.stripHtml(project.description || '').slice(0, 160);
    const canonicalUrl = `https://eplogoffplan.com/projects/${this.selectedProject?.project?.customUrl?.trim()?.split(' ')?.join('-')?.toLowerCase()}`; // Set canonical URL
    const ogImageUrl = this.selectedProject?.images[0]?.imageUrl || ''; // Set Open Graph Image
    this.titleService.setTitle(pageTitle);
    this.metaService.updateTag({ name: 'description', content: pageDescription });
    this.metaService.updateTag({ property: 'og:title', content: pageTitle });
    this.metaService.updateTag({ property: 'og:image', content: ogImageUrl });
    this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });
    this.metaService.updateTag({ property: 'og:type', content: 'product' });
    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: pageTitle });
    this.metaService.updateTag({ name: 'twitter:description', content: pageDescription });
    this.metaService.updateTag({ name: 'twitter:image', content: ogImageUrl });
    if (this.isBrowser && this.renderer) {
      // Create and append the canonical link tag
      let link: HTMLLinkElement = this.renderer.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canonicalUrl);
      this.renderer.appendChild(document.head, link);
    }else{
      const head = this.document.head;
      const existingLink = this.document.querySelector('link[rel="canonical"]');
      if (existingLink) {
        existingLink.setAttribute('href', canonicalUrl);
      } else {
        const link: HTMLLinkElement = this.document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', canonicalUrl);
        head.appendChild(link);
      }
    }
    this.upsertStructuredData(canonicalUrl, pageDescription);
  }

  private upsertStructuredData(canonicalUrl: string, description: string): void {
    const project = this.selectedProject?.project || {};
    const images = (this.selectedProject?.images || [])
      .map((image: any) => image?.imageUrl)
      .filter(Boolean)
      .slice(0, 10);
    const currency = project?.country?.currencyCode || project?.country?.currency || 'AED';
    const price = Number(String(project?.price || '').replace(/[^0-9.]/g, ''));

    const product: any = {
      '@type': 'Product',
      '@id': `${canonicalUrl}#property`,
      name: project.projectName,
      description,
      url: canonicalUrl,
      image: images,
      brand: {
        '@type': 'Organization',
        name: project?.developer?.name || 'Eplog Properties'
      },
      category: 'Real Estate Development'
    };

    if (Number.isFinite(price) && price > 0) {
      product.offers = {
        '@type': 'Offer',
        url: canonicalUrl,
        price,
        priceCurrency: currency,
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'RealEstateAgent', name: 'Eplog Properties' }
      };
    }

    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        product,
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://eplogoffplan.com/' },
            { '@type': 'ListItem', position: 2, name: 'Off-Plan Properties', item: 'https://eplogoffplan.com/offplanproject' },
            { '@type': 'ListItem', position: 3, name: project.projectName, item: canonicalUrl }
          ]
        }
      ]
    };

    this.document.getElementById('project-structured-data')?.remove();
    const script = this.document.createElement('script');
    script.id = 'project-structured-data';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    this.document.head.appendChild(script);
  }

  private stripHtml(value: string): string {
    return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }


  checkIfRouteExists(path: string): boolean {
    return this.router.config.some(route => route.path === path);
  }
}
